import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiFetch } from '@/lib/api';

/**
 * Ausschreibungen vom Server (/api/ausschreibungen).
 *
 * Eine Ausschreibung ist EIN Vorgang je Kunde: mit der erteilten Vollmacht
 * fragt der Makler bei mehreren Versorgern konkrete Preise an, vergleicht die
 * Rueckläufe, waehlt einen Gewinner und rechnet daraus die Provision aus.
 *
 * Der Server ist die Quelle der Wahrheit — es gibt KEINEN localStorage-Fallback.
 *
 * WICHTIG — zwei getrennte Fehlerzustaende (wie in useLeads):
 *   loadError  : nur das Laden ist fehlgeschlagen. Es gibt keine belastbaren
 *                Daten, also darf die Liste nicht angezeigt werden. Kennzahlen
 *                muessen dann "–" zeigen, niemals "0" — ein Fehler darf nie wie
 *                "keine Daten" aussehen.
 *   writeError : eine Aenderung (anlegen/aendern/loeschen) ist fehlgeschlagen.
 *                Die geladenen Daten sind weiterhin gueltig und bleiben sichtbar;
 *                der Fehler erscheint nur als Banner darueber.
 * Zusaetzlich WERFEN alle Mutationen, damit die aufrufende Seite den Fehler auch
 * lokal (z.B. in einem Dialog) behandeln kann.
 */

export const AUSSCHREIBUNG_STATUSES = [
  { value: 'offen',         label: 'Offen',        cls: 'bg-gray-100 text-gray-700' },
  { value: 'angebote_da',   label: 'Angebote da',  cls: 'bg-blue-100 text-blue-700' },
  { value: 'abgeschlossen', label: 'Abgeschlossen', cls: 'bg-green-100 text-green-700' },
  { value: 'verloren',      label: 'Verloren',     cls: 'bg-red-100 text-red-700' },
];

export const ANGEBOT_STATUSES = [
  { value: 'angefragt',  label: 'Angefragt',  cls: 'bg-gray-100 text-gray-700' },
  { value: 'angeboten',  label: 'Angeboten',  cls: 'bg-blue-100 text-blue-700' },
  { value: 'abgelehnt',  label: 'Abgelehnt',  cls: 'bg-red-100 text-red-700' },
  { value: 'abgelaufen', label: 'Abgelaufen', cls: 'bg-amber-100 text-amber-800' },
];

export const PROVISION_STATUSES = [
  { value: 'erwartet',   label: 'Erwartet' },
  { value: 'bestaetigt', label: 'Bestaetigt' },
  { value: 'ausgezahlt', label: 'Ausgezahlt' },
];

/**
 * Felder, die POST/PUT /api/ausschreibungen serverseitig entgegennimmt.
 * Alles andere (id, nummer, created_by, Zeitstempel und die vom Server
 * gerechneten Provisionsbetraege) wird nicht mitgeschickt.
 */
export const AUSSCHREIBUNG_FIELDS = [
  'lead_id', 'kalkulation_id', 'share_slug',
  'company_name', 'contact_name',
  'medium', 'plz', 'verbrauch_kwh', 'zaehlverfahren', 'hoechstleistung_kw',
  'spannungsebene', 'lieferbeginn', 'laufzeiten', 'angebotstyp', 'malo_id',
  'ziel_provision_ct_kwh',
  'status', 'gewinner_angebot_id', 'vertragsbeginn', 'vertragsende',
  'provision_ct_kwh', 'provision_status', 'provision_zahldatum',
  'verloren_grund', 'notizen',
];

const DATE_FIELDS = ['lieferbeginn', 'vertragsbeginn', 'vertragsende', 'provision_zahldatum'];
const NUMBER_FIELDS = [
  'lead_id', 'kalkulation_id', 'verbrauch_kwh', 'hoechstleistung_kw',
  'ziel_provision_ct_kwh', 'gewinner_angebot_id', 'provision_ct_kwh',
];

/** Leere Strings zu null — DATE-/NUMERIC-Spalten akzeptieren '' nicht. */
const normalizeValue = (key, value) => {
  if (DATE_FIELDS.includes(key)) return value ? value : null;
  if (NUMBER_FIELDS.includes(key)) {
    if (value === '' || value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return value;
};

/** Nur die serverseitig bekannten Felder uebernehmen. */
const buildPayload = (changes) => {
  const payload = {};
  for (const key of AUSSCHREIBUNG_FIELDS) {
    if (changes && Object.prototype.hasOwnProperty.call(changes, key)) {
      payload[key] = normalizeValue(key, changes[key]);
    }
  }
  return payload;
};

/**
 * Postgres liefert NUMERIC als String ("1234.56") — vor jeder Rechnung
 * durch diesen Filter. Fehlender/ungueltiger Wert ergibt null, nicht 0:
 * "nicht bekannt" und "null Euro" sind verschiedene Aussagen.
 */
export const toNum = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Einheitliche Provisionsformel (gilt im ganzen Projekt):
 *   provision_jahr_eur   = provision_ct_kwh / 100 * verbrauch_kwh
 *   provision_gesamt_eur = provision_jahr_eur * (laufzeit_monate / 12)
 * laufzeit_monate stammt vom Gewinner-Angebot.
 * Fehlt ein Wert -> das jeweilige Ergebnis ist null, NICHT 0.
 */
export const berechneProvision = ({ provision_ct_kwh, verbrauch_kwh, laufzeit_monate }) => {
  const ct = toNum(provision_ct_kwh);
  const kwh = toNum(verbrauch_kwh);
  const monate = toNum(laufzeit_monate);

  const jahr = ct === null || kwh === null ? null : (ct / 100) * kwh;
  const gesamt = jahr === null || monate === null ? null : jahr * (monate / 12);

  return { provision_jahr_eur: jahr, provision_gesamt_eur: gesamt };
};

/** Fehlermeldung aus einer fehlgeschlagenen Antwort ziehen. */
const readError = async (resp, fallback) => {
  try {
    const data = await resp.json();
    return data?.error || fallback;
  } catch {
    return fallback;
  }
};

export const useAusschreibungen = () => {
  const [ausschreibungen, setAusschreibungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [writeError, setWriteError] = useState('');

  const clearWriteError = useCallback(() => setWriteError(''), []);

  /**
   * Schreibfehler festhalten UND als Exception zurueckgeben, damit die
   * aufrufende Seite ihn zusaetzlich lokal behandeln kann.
   */
  const writeFailure = useCallback((message, fallback) => {
    const msg = message || fallback;
    setWriteError(msg);
    return new Error(msg);
  }, []);

  // Synchroner Spiegel des States: fuer optimistische Updates und das
  // Zuruecksetzen bei Fehlern (setState-Updater laufen erst beim Rendern).
  const listRef = useRef([]);
  const commit = useCallback((next) => {
    listRef.current = next;
    setAusschreibungen(next);
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const resp = await apiFetch('/api/ausschreibungen');
      if (!resp.ok) throw new Error(await readError(resp, 'Ausschreibungen konnten nicht geladen werden'));
      const data = await resp.json();
      listRef.current = Array.isArray(data) ? data : [];
      setAusschreibungen(listRef.current);
      // Nur den LADEfehler zuruecksetzen: ein offener Schreibfehler bleibt
      // stehen, bis der Nutzer ihn gesehen und weggeklickt hat.
      setLoadError('');
    } catch (err) {
      listRef.current = [];
      setAusschreibungen([]);
      setLoadError(err.message || 'Ausschreibungen konnten nicht geladen werden');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const reload = useCallback(() => load(), [load]);

  useEffect(() => { load(); }, [load]);

  /** Neue Ausschreibung anlegen. Wirft bei Fehlschlag. */
  const createAusschreibung = useCallback(async (data) => {
    const payload = buildPayload(data);
    if (!payload.company_name) {
      throw writeFailure('', 'Ohne Kundenname kann keine Ausschreibung angelegt werden');
    }

    let created;
    try {
      const resp = await apiFetch('/api/ausschreibungen', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!resp.ok) throw new Error(await readError(resp, 'Ausschreibung konnte nicht angelegt werden'));
      created = await resp.json();
      if (!created?.id) throw new Error('Server hat keine Ausschreibung zurueckgegeben');
    } catch (err) {
      throw writeFailure(err.message, 'Ausschreibung konnte nicht angelegt werden');
    }

    // Falls zwischenzeitlich neu geladen wurde, den Vorgang nicht doppelt fuehren.
    commit([created, ...listRef.current.filter(a => String(a.id) !== String(created.id))]);
    setWriteError('');
    return created;
  }, [commit, writeFailure]);

  /** Teil-Update. Nur gesendete (und serverseitig bekannte) Felder. Wirft bei Fehlschlag. */
  const updateAusschreibung = useCallback(async (id, changes) => {
    const payload = buildPayload(changes);
    if (Object.keys(payload).length === 0) {
      throw writeFailure('', 'Keine serverseitig speicherbaren Felder enthalten');
    }

    const snapshot = listRef.current;
    const target = snapshot.find(a => String(a.id) === String(id));
    if (!target) {
      throw writeFailure('', 'Ausschreibung nicht gefunden');
    }

    // Optimistisch anwenden …
    commit(snapshot.map(a =>
      String(a.id) === String(id)
        ? { ...a, ...payload, updated_at: new Date().toISOString() }
        : a
    ));

    let saved;
    try {
      const resp = await apiFetch(`/api/ausschreibungen/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (!resp.ok) throw new Error(await readError(resp, 'Aenderung konnte nicht gespeichert werden'));
      saved = await resp.json();
      if (!saved?.id) throw new Error('Ausschreibung auf dem Server nicht gefunden');
    } catch (err) {
      // … und bei Fehler wieder zuruecknehmen.
      commit(snapshot);
      throw writeFailure(err.message, 'Aenderung konnte nicht gespeichert werden');
    }

    // Server-Zeile uebernehmen. Die Listen-Kennzahlen (angebote_anzahl,
    // angebote_eingegangen, bester_preis_ct_kwh, created_by_name) liefert nur
    // GET /api/ausschreibungen — vorhandene Werte deshalb nicht wegwerfen.
    commit(listRef.current.map(a =>
      String(a.id) === String(id) ? { ...a, ...saved } : a
    ));
    setWriteError('');
    return saved;
  }, [commit, writeFailure]);

  /** Ausschreibung loeschen. Wirft bei Fehlschlag. */
  const removeAusschreibung = useCallback(async (id) => {
    const snapshot = listRef.current;
    commit(snapshot.filter(a => String(a.id) !== String(id)));

    try {
      const resp = await apiFetch(`/api/ausschreibungen/${id}`, { method: 'DELETE' });
      if (!resp.ok) throw new Error(await readError(resp, 'Ausschreibung konnte nicht geloescht werden'));
    } catch (err) {
      commit(snapshot);
      throw writeFailure(err.message, 'Ausschreibung konnte nicht geloescht werden');
    }

    setWriteError('');
  }, [commit, writeFailure]);

  // Server-IDs sind Zahlen, useParams liefert Strings — immer als String vergleichen.
  const getAusschreibungById = useCallback((id) => {
    if (id === undefined || id === null) return null;
    return ausschreibungen.find(a => String(a.id) === String(id)) || null;
  }, [ausschreibungen]);

  /**
   * Kennzahlen: Anzahl je Status sowie die Provisionssummen je provision_status.
   * In die Provisionssummen zaehlen NUR abgeschlossene Ausschreibungen — bei
   * allem anderen ist die Provision noch nicht verdient.
   */
  const stats = useMemo(() => {
    const s = {
      gesamt: ausschreibungen.length,
      offen: 0,
      angebote_da: 0,
      abgeschlossen: 0,
      verloren: 0,
      provision_erwartet_eur: 0,
      provision_bestaetigt_eur: 0,
      provision_ausgezahlt_eur: 0,
    };

    ausschreibungen.forEach((a) => {
      const status = a.status || 'offen';
      if (Object.prototype.hasOwnProperty.call(s, status)) s[status]++;

      if (status !== 'abgeschlossen') return;
      const betrag = toNum(a.provision_gesamt_eur);
      if (betrag === null) return;

      const ps = a.provision_status || 'erwartet';
      if (ps === 'erwartet') s.provision_erwartet_eur += betrag;
      else if (ps === 'bestaetigt') s.provision_bestaetigt_eur += betrag;
      else if (ps === 'ausgezahlt') s.provision_ausgezahlt_eur += betrag;
    });

    return s;
  }, [ausschreibungen]);

  return {
    ausschreibungen,
    loading,
    loadError,
    writeError,
    clearWriteError,
    stats,
    createAusschreibung,
    updateAusschreibung,
    removeAusschreibung,
    getAusschreibungById,
    reload,
    // Kurz-Aliase — identische Funktionen, nur bequemer beim Destrukturieren.
    create: createAusschreibung,
    update: updateAusschreibung,
    remove: removeAusschreibung,
  };
};

export default useAusschreibungen;
